import React from 'react';
import { format } from 'date-fns';
import type { ScheduleProjectSummary } from '../../../../types/workflows';
import './ScheduleTooltip.css';

export interface ScheduleTooltipProps {
  project: ScheduleProjectSummary;
  position: { x: number; y: number };
  visible: boolean;
}

/**
 * ScheduleTooltip Component
 * 
 * Displays project details in a tooltip when hovering over timeline bars.
 * 
 * Features:
 * - Project name
 * - Progress percentage (completedCount / milestoneCount)
 * - Next milestone date (nextPlannedDate)
 * - Owner name
 * - Overdue count (if > 0)
 * - Positioned near cursor without obstructing view
 * - Respects reduced-motion settings
 * - Keyboard accessible
 */
const ScheduleTooltip: React.FC<ScheduleTooltipProps> = ({
  project,
  position,
  visible,
}) => {
  if (!visible) {
    return null;
  }

  // Use progress from backend if available, otherwise calculate it
  const progress = project.progress !== undefined
    ? project.progress
    : (project.milestoneCount > 0
        ? (project.completedCount / project.milestoneCount) * 100
        : 0);

  // Format next planned date
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <div
      className="schedule-tooltip"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
      role="tooltip"
      aria-label={`Project details for ${project.projectName}`}
    >
      <div className="tooltip-header">
        <h4 className="tooltip-project-name">{project.projectName}</h4>
        {project.projectType && (
          <span className="tooltip-project-type">{project.projectType}</span>
        )}
      </div>
      
      <div className="tooltip-content">
        <div className="tooltip-row">
          <span className="tooltip-label">Progress:</span>
          <span className="tooltip-value">
            {progress.toFixed(0)}% ({project.completedCount} / {project.milestoneCount})
          </span>
        </div>
        
        <div className="tooltip-row">
          <span className="tooltip-label">Next Milestone:</span>
          <span className="tooltip-value">
            {formatDate(project.nextPlannedDate)}
          </span>
        </div>
        
        {project.ownerName && (
          <div className="tooltip-row">
            <span className="tooltip-label">Owner:</span>
            <span className="tooltip-value">{project.ownerName}</span>
          </div>
        )}
        
        {project.clientName && (
          <div className="tooltip-row">
            <span className="tooltip-label">Client:</span>
            <span className="tooltip-value">{project.clientName}</span>
          </div>
        )}
        
        {project.overdueCount > 0 && (
          <div className="tooltip-row tooltip-overdue">
            <span className="tooltip-label">⚠️ Overdue:</span>
            <span className="tooltip-value overdue-count">
              {project.overdueCount} milestone{project.overdueCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        
        {project.disciplines && project.disciplines.length > 0 && (
          <div className="tooltip-row">
            <span className="tooltip-label">Disciplines:</span>
            <span className="tooltip-value">
              {project.disciplines.join(', ')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleTooltip;

